import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';

interface AttendanceRecord {
  id: string;
  status: string;
  sessionDate: string;
  startTime: string;
  sessionType?: string | null;
  courtNumber?: number | null;
  drillLog?: string[];
}

interface AttendanceTimelineProps {
  records: AttendanceRecord[];
  emptyMessage?: string;
}

const statusDotColor: Record<string, string> = {
  present: 'bg-green-500',
  late: 'bg-yellow-500',
  absent: 'bg-red-500',
};

const statusBadgeStyle: Record<string, { variant: 'default' | 'secondary' | 'destructive'; className: string }> = {
  present: { variant: 'default', className: 'bg-green-100 text-green-700' },
  late: { variant: 'secondary', className: 'bg-yellow-100 text-yellow-700' },
  absent: { variant: 'destructive', className: '' },
};

export function AttendanceTimeline({ records, emptyMessage = 'No attendance records yet.' }: AttendanceTimelineProps) {
  if (records.length === 0) {
    return <p className="text-center text-muted-foreground py-4">{emptyMessage}</p>;
  }

  return (
    <div className="space-y-2">
      {records.map(record => {
        const badge = statusBadgeStyle[record.status] ?? statusBadgeStyle.absent;
        const hasDrills = record.drillLog && record.drillLog.length > 0;
        return (
          <div
            key={record.id}
            className={cn('flex flex-col p-3 border rounded-lg', hasDrills ? 'gap-2' : '')}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className={cn('w-2.5 h-2.5 rounded-full shrink-0', statusDotColor[record.status] ?? 'bg-gray-500')} />
                <div>
                  <p className="text-sm font-medium">
                    {new Date(record.sessionDate).toLocaleDateString('en-US', {
                      weekday: 'short',
                      month: 'short',
                      day: 'numeric',
                    })}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {record.startTime}
                    {record.sessionType && ` \u2022 ${record.sessionType}`}
                    {record.courtNumber && ` \u2022 Court ${record.courtNumber}`}
                  </p>
                </div>
              </div>
              <Badge variant={badge.variant} className={cn('text-xs', badge.className)}>
                {record.status}
              </Badge>
            </div>
            {hasDrills && (
              <div className="pl-5 border-t pt-2">
                <p className="text-xs text-muted-foreground font-medium mb-1">Session Drills:</p>
                <ol className="space-y-0.5">
                  {record.drillLog!.map((drill, i) => (
                    <li key={i} className="text-xs text-muted-foreground">{i + 1}. {drill}</li>
                  ))}
                </ol>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
